import React from 'react';

import chapterImg3 from '../../../../assets/images/courses/team-players/chapter7.1.1.jpg';
import chapterImg2 from '../../../../assets/images/courses/team-players/chapter7.1.jpg';
import introImg from '../../../../assets/images/courses/team-players/chapter7.jpg';
import { CourseChapter } from '../../../../courses/courses.types';
import { CustomAccordion } from '../../../common/Accordion';
import FlipCard from '../../FlipCard';
import { InfoBlock } from '../../InfoBlock';
import { MediaBlock } from '../../MediaBlock';

interface TeamPlayersProps {
  chapter: CourseChapter;
  onNextChapter: (nextIndex: number) => void;
  totalPages: number;
  courseId?: string;
  onQuizResult?: (result: boolean | undefined) => void;
}

export const TeamPlayersChapter7: React.FC<TeamPlayersProps> = ({
  chapter,
  onNextChapter,
  totalPages,
}) => {
  const accordionData1 = [
    {
      id: 'panel1',
      title: 'Nico',
      image: '',
      content: `<p>There might be someone you find difficult and on reflection, it's because they're slow and dull. In their strengths column, you could put, good with detail. My Issue with Them: Slow and dull Their Strength: Good with detail</p>`,
    },
    {
      id: 'panel2',
      title: 'Andrea',
      image: '',
      content: `<p>The person you avoid because they're too pushy, always in a rush, making you feel under pressure, in their strengths column, you might put, good at getting me to see the big picture, or good at getting groups to make decisions. My Issue with Them: Pushy and always in a rush Their Strength: Effective at getting me to see the big picture</p>`,
    },
    {
      id: 'panel3',
      title: 'Carlos',
      image: '',
      content: `<p>The person that's a bit sensitive who gets upset easily, in their strengths column, you might put, good at spotting when I've forgotten to consider others in my plan, or good at including the quieter team members in the planning process. My Issue with Them: Easily upset Their Strength: Skilled at spotting when I’ve forgotten to consider others in my plan</p>`,
    },
  ];
  const handleNextChapterClick = () => {
    onNextChapter(chapter.index + 1);
  };

  const handlePrint = (view: string) => {
    const printArea = document.getElementById(view);
    if (printArea) {
      printArea.classList.add('print-view');

      const cleanup = () => {
        printArea.classList.remove('print-view');
        window.onafterprint = null;
      };

      window.onafterprint = cleanup;

      window.print();
    }
  };

  return (
    <>
      <div className="content-wrap lg:border-l lg:pl-8 lg:pt-8 pt-4 pb-10">
        <p className="text-sm italic  mb-7">
          Part {chapter.index} of {totalPages}
        </p>
        <h2 className="font-semibold text-3xl">Understand the Value Others Bring to the Team</h2>
        <MediaBlock mediaImage={introImg}></MediaBlock>

        <br />
        <h2 className="font-normal text-center text-2xl">
          Perhaps the foremost reason why teams are essential is that no one is flawless.{' '}
        </h2>

        <p className="text-xl text-left font-light mt-10 mb-10">
          It's impossible to excel in every aspect on your own because inevitably, there will be
          areas where you struggle. It might be creativity, follow-through, confronting intimidating
          situations, or long-term planning, among others. However, this diversity is what makes
          life intriguing. It ensures that there's someone skilled and passionate about each task,
          using their strengths towards successful outcomes.
        </p>
        <br />
        <br />
        <div className="md:flex items-center mt-10 mb-10">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg2} alt="" />
          </div>
          <p className="flex-1 leading-7">
            <br />
            <br />
            Yet, within a team, it's crucial to cultivate tolerance for each other's shortcomings.
            Just because decision-making comes naturally to you doesn't mean it's effortless for
            everyone else, and it's unjust to criticize them for it. Instead, view their challenges
            as opportunities for you to shine.
          </p>
        </div>
        <div className="md:flex items-center mt-10 mb-10">
          <p className="flex-1 leading-7">
            <br />
            <br />
            Perhaps they lack attention to detail, while you excel in it – that's a complementary
            dynamic. Rather than wasting energy faulting them, acknowledge that in a team, everyone
            brings unique strengths. Imperfections are inherent, but through teamwork, perfection
            becomes attainable.
          </p>
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg3} alt="" />
          </div>
        </div>
        <h3 className="text-2xl text-purple-600 font-bold mb-1">
          Managing Irritating Team Members
        </h3>
        <br />
        <br />
        <p className="text-lg text-center font-bold mt-10 mb-10">
          It's easy to find some people irritating, especially if they're different to you. This
          might be a difference of, for example, the detail person versus the speedy one, the hurry
          up person, or the harsh person versus the sympathetic type of person.
        </p>
        <h3 className="text-2xl text-purple-600 font-bold mb-1">Weakness Assessment</h3>
        <p className="text-lg text-center font-bold mt-10 mb-10">
          It's easy to find some people irritating, especially if they're different to you. This
          might be a difference of, for example, the detail person versus the speedy one, the hurry
          up person, or the harsh person versus the sympathetic type of person.
        </p>
        <br />

        <div className="flex justify-center gap-5">
          <FlipCard>
            <div className="front-content">
              <p>Does the harsh person need a sympathetic person to work with?</p>
            </div>
            <div className="front-content">
              <p>Well, somethimes. Otherwise, they can miss things.</p>
            </div>
          </FlipCard>
          <FlipCard>
            <div className="front-content">
              <p>Does the sympathetic person need the harsh person?</p>
            </div>
            <div className="front-content">
              <p>Well, yes, sometimes to make those tough decisions.</p>
            </div>
          </FlipCard>
        </div>

        <p className="text-lg text-center font-semibold mt-10 mb-10">
          The person who's different to you is exactly the person you need on your team. The detail
          person needs the fast person and the fast person needs the detail person.
        </p>

        <h2 className="font-normal text-center text-2xl">
          The key is to see them not as annoying, but as having just the skills that you need for
          the team to achieve its’ goals.
        </h2>
        <br />
        <InfoBlock
          title="Note: "
          text="We will explore more about difficulties that team face and strategies to help you deal with these types of situations."
        />

        <h3 className="text-2xl text-purple-600 font-bold mt-10">
          Activity: Appreciate Different Strengths
        </h3>
        <p className="text-lg text-left font-semibold mt-10 mb-10">
          Who do you find difficult? Can you change this perspective in your mind and see the
          benefit of that other person? Complete the chart below, list out who the person is, why
          you find them difficult, and perhaps based on why you find them difficult, what strengths
          they must contribute?
        </p>
        <p className="text-xl font-bold mb-8">
          Click on the '+' signs to see three examples below to help you get started.
        </p>
        {accordionData1.map((data) => (
          <CustomAccordion
            key={data.title}
            title={data.title}
            content={data.content}
            id={data.id}
            image={data?.image}
          />
        ))}

        <br />
        <br />
        <div id="print-view1">
          <table className="seek-information w-full">
            <thead>
              <tr>
                <th style={{ backgroundColor: 'blueviolet', color: 'whitesmoke' }}>Name</th>
                <th style={{ backgroundColor: 'blueviolet', color: 'whitesmoke' }}>
                  My issues with Them
                </th>
                <th style={{ backgroundColor: 'blueviolet', color: 'whitesmoke' }}>
                  Their Strengths
                </th>
              </tr>
            </thead>
            <tbody className="bg-purple-100">
              <tr>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>

                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
              </tr>
              <tr>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
              </tr>
              <tr>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
              </tr>
            </tbody>
          </table>
          <div className="mt-5 text-right">
            <button onClick={() => handlePrint('print-view1')} className="btn-internal">
              PRINT
            </button>
          </div>
        </div>
        <br />
        <InfoBlock
          title=""
          text="This practice will help you to see the strengths of your team members and maybe help you to use them in the right way and for the right situations. "
        />
        <br />
        <h2 className="font-normal text-center text-2xl mt-10 mb-10 leading-7">
          Perfection in teams is rare; it's challenging to engineer flawless teams, even with
          authority. Often, teams are formed haphazardly or based on personal affinities, which may
          not yield optimal outcomes. Nevertheless, there's potential for improvement. It starts
          with accepting that others may struggle with tasks we find effortless and appreciating the
          different strengths amongst your team members.
        </h2>

        <h3 className="text-2xl text-purple-600 font-bold m-5">Reflect</h3>
        <p className="text-xl font-light flex-1 mt-5 mb-5">
          Reflect and answer the following questions. You may print your responses upon completion.
        </p>

        <div id="print-view2">
          <ol className="list-decimal pl-7 mb-14 text-xl font-light">
            <li className="mb-5">
              {' '}
              <div>
                <p className="m-4">
                  Reflect on individuals whose faults irk you - can you shift your perspective to
                  appreciate their strengths instead?
                </p>
                <textarea
                  className="w-full h-32 p-2 border rounded"
                  placeholder="Enter your text here..."
                ></textarea>
              </div>
            </li>
            <li className="mb-5">
              {' '}
              <div>
                <p className="m-4">
                  Moreover, can you channel your irritation into appreciation for your own abilities
                  in areas where they falter?
                </p>
                <textarea
                  className="w-full h-32 p-2 border rounded"
                  placeholder="Enter your text here..."
                ></textarea>
              </div>
            </li>
          </ol>

          <div className="mt-5 text-right">
            <button onClick={() => handlePrint('print-view2')} className="btn-internal">
              PRINT
            </button>
          </div>
        </div>

        <br />
        <button onClick={handleNextChapterClick} className="btn-internal w-full">
          Conclusion
        </button>
      </div>
    </>
  );
};
